import Collapse from "@material-ui/core/Collapse";
import { makeStyles } from "@material-ui/core/styles";
import { RefObject, useEffect, useRef, useState } from "react";
import { sleepAsync } from "../../../../../../../../../common/functions";
import { splitByBoolean } from "../../../../../../../../../common/util/Array/orderByBoolean";
import { isAndroid } from "../../../../../../../../../common/util/osCheck";
import ShurikenProgress from "../../../../../../../Animations/ShurikenProgress";
import { User, UserForRanking } from "../../../../../../../User/types";
import {
    ChangeCommenterAvatarElement,
    OriginKey,
    Post,
    PostComment,
} from "../../../../types";
import { CommentCard, collapseTimeStep, collapseTimeout } from "./CommentCard";
import { checkUnreadComment, getSendText } from "./CommentCard/functions";
import { InputFieldArea } from "./InputFieldArea";

export function CommentArea({
    user,
    addCommentCount,
    post,
    tmpMutedUserIds,
    setTmpMutedUserIds,
    unreadCommentNos,
    isInputAreaHidden,
    changeCommenterAvatarElement,
    refreshUsersConnectionHeightList,
    comments,
    setComments,
    commentedUsers,
    setCommentedUsers,
    showOnlyUnreadComment,
    setShowOnlyUnreadComment,
    areUnreadCommentsRemoved,
    addTmpComment,
    scrollableContainerRef,
    originKey,
}: {
    user?: User;
    addCommentCount: (addedCount: number) => void;
    post: Post;
    tmpMutedUserIds: number[];
    setTmpMutedUserIds: (ids: number[]) => void;
    unreadCommentNos: number[];
    isInputAreaHidden: boolean;
    changeCommenterAvatarElement: ChangeCommenterAvatarElement;
    refreshUsersConnectionHeightList: () => void;
    comments: PostComment[];
    setComments: (comments: PostComment[]) => void;
    commentedUsers: UserForRanking[];
    setCommentedUsers: (commentedUsers: UserForRanking[]) => void;
    showOnlyUnreadComment: boolean;
    setShowOnlyUnreadComment: (showOnlyUnreadComment: boolean) => void;
    areUnreadCommentsRemoved: boolean;
    addTmpComment: (tmpComment: PostComment) => void;
    scrollableContainerRef: RefObject<HTMLDivElement>;
    originKey: OriginKey;
}) {
    const [loaded, setLoaded] = useState(false);

    const { userId: postedUserId, postNo, likeCount, commentCount } = post;
    const userId = user?.userId ?? 0;
    useEffect(() => {
        fetch(
            `api/PostComment/GetCommentsForPost?PostedUserId=${postedUserId}&PostNo=${postNo}&UserId=${userId}`
        )
            .then(p => p.json())
            .then(r => {
                setComments(r.comments);
                setCommentedUsers(r.commentedUsers);
                setLoaded(true);
            });
    }, [postedUserId, postNo, likeCount, commentCount, userId]);

    const c = useStyles();

    const [isCollapseOpen, setCollapseOpen] = useState(false);
    useEffect(() => {
        setCollapseOpen(true);
    }, []);

    const inputRef = useRef<HTMLInputElement>(null);

    /**
     * Calculate refreshUsersConnectionHeightList after scrolling
     */
    const timerIdRef = useRef<number | null>(null); // For debounce
    useEffect(() => {
        if (!isCollapseOpen) {
            return; // No need to recalculate for hidden comments
        }
        const scrollableElement = scrollableContainerRef.current;
        if (!scrollableElement) {
            return;
        }
        const onScroll = () => {
            if (typeof timerIdRef.current === "number") {
                clearTimeout(timerIdRef.current); // Prevent to calculate too often
            }
            timerIdRef.current = window.setTimeout(() => {
                refreshUsersConnectionHeightList();
            }, 500);
        };
        scrollableElement.addEventListener("scroll", onScroll);
        return () => {
            scrollableElement.removeEventListener("scroll", onScroll);
        };
    }, [scrollableContainerRef.current, isCollapseOpen]);

    return (
        <Collapse
            in={isCollapseOpen}
            onEntered={() => {
                if (isAndroid) {
                    return; // focusをするとアンドロイドではソフトウェアキーボードが出てしまう問題がある
                }
                inputRef.current?.focus();
            }}
        >
            <div
                className={c.container}
                onClick={ev => {
                    ev.stopPropagation();
                }}
            >
                {!isInputAreaHidden && (
                    <InputFieldArea
                        inputRef={inputRef}
                        user={user}
                        sendText={getSendText(
                            addTmpComment,
                            post,
                            addCommentCount
                        )}
                        className={c.inputField}
                        placeholder={"Write a comment..."}
                        onEdit={refreshUsersConnectionHeightList}
                        autoFocus={isAndroid}
                    />
                )}

                <CommentList
                    comments={comments}
                    setComments={setComments}
                    setCommentedUsers={setCommentedUsers}
                    commentedUsers={commentedUsers}
                    post={post}
                    loaded={loaded}
                    user={user}
                    tmpMutedUserIds={tmpMutedUserIds}
                    setTmpMutedUserIds={setTmpMutedUserIds}
                    addCommentCount={addCommentCount}
                    unreadCommentNos={
                        areUnreadCommentsRemoved ? [] : unreadCommentNos
                    }
                    showOnlyUnreadComment={showOnlyUnreadComment}
                    setShowOnlyUnreadComment={setShowOnlyUnreadComment}
                    changeCommenterAvatarElement={changeCommenterAvatarElement}
                    refreshUsersConnectionHeightList={
                        refreshUsersConnectionHeightList
                    }
                    addTmpComment={addTmpComment}
                    originKey={originKey}
                />
            </div>
        </Collapse>
    );
}
const useStyles = makeStyles(theme => ({
    container: {
        width: "100%",
        marginTop: 5,
        paddingRight: 5,
        borderTop: "solid",
        borderTopColor: theme.palette.grey[100],
        borderTopWidth: 1,
    },
    inputField: { marginTop: 10, marginBottom: 15 },
}));

function CommentList({
    comments,
    setComments,
    setCommentedUsers,
    post,
    loaded,
    commentedUsers,
    user,
    tmpMutedUserIds,
    setTmpMutedUserIds,
    addCommentCount,
    unreadCommentNos,
    showOnlyUnreadComment,
    setShowOnlyUnreadComment,
    changeCommenterAvatarElement,
    refreshUsersConnectionHeightList,
    addTmpComment,
    originKey,
}: {
    comments: PostComment[];
    setComments: (comments: PostComment[]) => void;
    setCommentedUsers: (users: UserForRanking[]) => void;
    post: Post;
    loaded: boolean;
    commentedUsers: UserForRanking[];
    user?: User;
    tmpMutedUserIds: number[];
    setTmpMutedUserIds: (ids: number[]) => void;
    addCommentCount: (addedCount: number) => void;
    unreadCommentNos: number[];
    showOnlyUnreadComment: boolean;
    setShowOnlyUnreadComment: (onlyUnread: boolean) => void;
    changeCommenterAvatarElement: ChangeCommenterAvatarElement;
    refreshUsersConnectionHeightList: () => void;
    addTmpComment: (tmpComment: PostComment) => void;
    originKey: OriginKey;
}) {
    const commentsToPost = comments.filter(com => !com.replyTargetCommentNo);

    const [unreadComments, readComments] = splitByBoolean(commentsToPost, com =>
        checkUnreadComment(com, unreadCommentNos, comments)
    );
    const commentsToShow = showOnlyUnreadComment
        ? unreadComments
        : unreadComments.concat(readComments);

    const c = useListStyles();

    const isNoComment = post.likeCount === 0 && post.commentCount === 0;
    if (!loaded && !isNoComment) {
        return <ShurikenProgress size="7%" style={{ marginBottom: 15 }} />;
    }

    if (comments.length < 1) {
        return null;
    }

    const deleteComment = async (
        targetComment: PostComment,
        deleteFromServerSide: () => Promise<void>
    ) => {
        setComments(
            comments.filter(com => com.commentNo !== targetComment.commentNo)
        );
        await deleteFromServerSide();

        if (!targetComment.isLike) {
            addCommentCount(-1); // Trigger fetch new value
        }
    };

    const keepRefreshing = async () => {
        for (let i = 0; i < collapseTimeout; i += collapseTimeStep) {
            await sleepAsync(collapseTimeStep);
            refreshUsersConnectionHeightList();
        }
    };

    return (
        <div className={c.container}>
            {commentsToShow.map(comment => {
                return (
                    <CommentCard
                        key={comment.commentNo}
                        commentedUsers={commentedUsers}
                        post={post}
                        comment={comment}
                        user={user}
                        tmpMutedUserIds={tmpMutedUserIds}
                        setTmpMutedUserIds={setTmpMutedUserIds}
                        comments={comments}
                        setComments={setComments}
                        setCommentedUsers={setCommentedUsers}
                        deleteComment={deleteComment}
                        changeCommenterAvatarElement={
                            changeCommenterAvatarElement
                        }
                        refreshUsersConnectionHeightList={
                            refreshUsersConnectionHeightList
                        }
                        unreadCommentNos={unreadCommentNos}
                        showOnlyUnreadComment={showOnlyUnreadComment}
                        addTmpComment={addTmpComment}
                        addCommentCount={addCommentCount}
                        setShowOnlyUnreadComment={setShowOnlyUnreadComment}
                        keepRefreshing={keepRefreshing}
                        originKey={originKey}
                    />
                );
            })}

            {showOnlyUnreadComment &&
                comments.length !== commentsToShow.length && (
                    <div
                        className={c.showMore}
                        onClick={() => {
                            setShowOnlyUnreadComment(false);
                            keepRefreshing();
                        }}
                    >
                        Show more
                    </div>
                )}
        </div>
    );
}
const useListStyles = makeStyles(theme => ({
    showMore: {
        width: "100%",
        display: "flex",
        justifyContent: "center",
        color: theme.palette.primary.main,
        cursor: "pointer",
        position: "relative",
        bottom: 2,
    },
    container: { marginRight: 5 },
}));
